import { createSelector } from 'reselect';

import { APP_INITIAL_STATE } from './reducer';

const selectRouter = (state) => state.router;

const selectAppDomain = (state) => state.app || APP_INITIAL_STATE;

const makeSelectAppState = () =>
  createSelector(selectAppDomain, (substate) => substate);

const makeSelectLocation = () =>
  createSelector(selectRouter, (routerState) => routerState.location);

const makeSelectLoadingState = () =>
  createSelector(selectAppDomain, (substate) => substate.loading);

const makeSelectUser = () =>
  createSelector(selectAppDomain, (substate) => substate.user);

const makeSelectUserRole = () =>
  createSelector(selectAppDomain, (substate) => substate.currentUserRole);

const makeSelectLdIdentifiedContext = () =>
  createSelector(selectAppDomain, (substate) => substate.ldIdentifiedContext);

const makeSelectMembersInfo = () =>
  createSelector(selectAppDomain, (substate) => substate.membersInfo);

const makeSelectActiveOrgResourceId = () =>
  createSelector(
    selectAppDomain,
    (substate) =>
      substate.activeOrganization && substate.activeOrganization.orgResourceId,
  );

const makeSelectActiveOrganization = () =>
  createSelector(
    selectAppDomain,
    (substate) => substate.activeOrganization && substate.activeOrganization,
  );
const makeSelectServiceLevels = () =>
  createSelector(selectAppDomain, (substate) => substate.levelsOfServiceStates);

const makeSelectUnreadNotifications = () =>
  createSelector(selectAppDomain, (substate) => substate.unreadNotifications);

const makeSelectLocationModifiers = () =>
  createSelector(selectAppDomain, (substate) => substate.locationModifiers);

const makeSelectOrgCoordinates = () =>
  createSelector(
    selectAppDomain,
    (substate) => substate.activeOrganization?.address?.longLat || null,
  );

const makeSelectAssignees = () =>
  createSelector(selectAppDomain, (substate) => substate.assignees);

const makeSelectContracts = () =>
  createSelector(selectAppDomain, (substate) => substate.contracts);

const makeSelectMemberPreferences = () =>
  createSelector(selectAppDomain, (substate) => substate.memberPreferences);

const makeSelectMemberPreferencesLoading = () =>
  createSelector(
    selectAppDomain,
    (substate) => substate.isLoadingMemberPreferences,
  );
const makeSelectEstimates = () =>
  createSelector(selectAppDomain, (substate) => substate.estimates);

const makeSelectTncContracts = () =>
  createSelector(selectAppDomain, (substate) => substate.tncContracts);

const makeSelectAppointReasons = () =>
  createSelector(selectAppDomain, (substate) => substate.appointmentReasons);

const makeSelectIsLoadingAppointmentReasons = () =>
  createSelector(
    selectAppDomain,
    (substate) => substate.isLoadingAppointmentReasons,
  );

// TODO: TS-5173: Remove globalSearchFilter
const makeSelectGlobalSearchFilter = () =>
  createSelector(selectAppDomain, (substate) => substate.globalSearchFilter);

const makeSelectTimezone = () =>
  createSelector(
    selectAppDomain,
    (substate) => substate.activeOrganization?.timezone,
  );

const makeSelectTripPreferences = () =>
  createSelector(selectAppDomain, (substate) => substate.tripPreferences);

const makeSelectTripCancellationReasons = () =>
  createSelector(
    selectAppDomain,
    (substate) => substate.tripCancellationReasons,
  );

const makeSelectTripDenialReasons = () =>
  createSelector(selectAppDomain, (substate) => substate.tripDenialReasons);
const makeSelectTripEscalationReasons = () =>
  createSelector(selectAppDomain, (substate) => substate.tripEscalationReasons);
const makeSelectTripApprovalReasons = () =>
  createSelector(selectAppDomain, (substate) => substate.tripApprovalReasons);

const makeSelectSidebarMenuCounts = () =>
  createSelector(selectAppDomain, (substate) => substate?.sidebarMenuCounts);

const makeSelectVehicleCategories = () =>
  createSelector(selectAppDomain, (substate) => substate?.vehicleCategories);

const makeSelectIntegrationState = () =>
  createSelector(selectAppDomain, (substate) => substate?.integrationState);

const makeSelectTripGroupingState = () =>
  createSelector(selectAppDomain, (substate) => substate?.tripGroupingState);

const makeSelectMemberEnrollmentCoverageTripCounts = () =>
  createSelector(
    selectAppDomain,
    (substate) => substate?.memberEnrollmentCoverageTripCounts,
  );

const makeSelectGlobalSearch = () =>
  createSelector(selectAppDomain, (substate) => substate.globalSearch);

const makeSelectToastNotifications = () =>
  createSelector(selectAppDomain, (substate) => substate.toastNotifications);

const makeSelectLoadingUser = () =>
  createSelector(selectAppDomain, (substate) => substate.isLoadingUser);

/**
 * The app-slice fields the root <App> component actually renders.
 *
 * App previously used makeSelectAppState, which is
 * `createSelector(selectAppDomain, (substate) => substate)` -- the WHOLE slice.
 * That subscribed the root component to every field in it, so any app-slice
 * write re-rendered the entire application tree. PrivateContainer fires 14
 * fetches on mount (levels of service, assignees, org custom fields, ...), each
 * with a request and a response write, so a single page load produced ~28
 * full-tree re-renders -- and every one re-instantiated the ~497
 * `useSelector(makeSelectX())` call sites and recomputed the ~153 that build a
 * fresh object. That allocation rate, not retention, is what exhausted the
 * renderer (TS-9404).
 *
 * Memoised on the 18 fields below, so writes to anything else in the slice no
 * longer reach App. Exported as an INSTANCE, not a factory, so it cannot be
 * re-created per render.
 */
export const selectAppShellState = createSelector(
  [
    (state) => selectAppDomain(state).user,
    (state) => selectAppDomain(state).currentUserRole,
    (state) => selectAppDomain(state).organizations,
    (state) => selectAppDomain(state).inactivityModalVisibility,
    (state) => selectAppDomain(state).activeOrganization,
    (state) => selectAppDomain(state).messages,
    (state) => selectAppDomain(state).redirectUrl,
    (state) => selectAppDomain(state).redirectPageName,
    (state) => selectAppDomain(state).alertMessages,
    (state) => selectAppDomain(state).appNotifications,
    (state) => selectAppDomain(state).loading,
    (state) => selectAppDomain(state).isLoadingUser,
    (state) => selectAppDomain(state).isLoadingOrganizations,
    (state) => selectAppDomain(state).isLoadingAccessToken,
    (state) => selectAppDomain(state).activeTab,
    (state) => selectAppDomain(state).activeMenu,
    (state) => selectAppDomain(state).expanded,
    (state) => selectAppDomain(state).totalUnreadMessageCount,
  ],
  (
    user,
    currentUserRole,
    organizations,
    inactivityModalVisibility,
    activeOrganization,
    messages,
    redirectUrl,
    redirectPageName,
    alertMessages,
    appNotifications,
    loading,
    isLoadingUser,
    isLoadingOrganizations,
    isLoadingAccessToken,
    activeTab,
    activeMenu,
    expanded,
    totalUnreadMessageCount,
  ) => ({
    user,
    currentUserRole,
    organizations,
    inactivityModalVisibility,
    activeOrganization,
    messages,
    redirectUrl,
    redirectPageName,
    alertMessages,
    appNotifications,
    loading,
    isLoadingUser,
    isLoadingOrganizations,
    isLoadingAccessToken,
    activeTab,
    activeMenu,
    expanded,
    totalUnreadMessageCount,
  }),
);

export {
  makeSelectLocation,
  makeSelectLoadingState,
  makeSelectAppState,
  makeSelectUser,
  makeSelectMembersInfo,
  makeSelectActiveOrgResourceId,
  makeSelectServiceLevels,
  makeSelectUnreadNotifications,
  makeSelectActiveOrganization,
  makeSelectLocationModifiers,
  makeSelectOrgCoordinates,
  makeSelectAssignees,
  makeSelectContracts,
  makeSelectMemberPreferences,
  makeSelectMemberPreferencesLoading,
  makeSelectAppointReasons,
  makeSelectIsLoadingAppointmentReasons,
  makeSelectEstimates,
  makeSelectTncContracts,
  makeSelectUserRole,
  makeSelectLdIdentifiedContext,
  makeSelectGlobalSearchFilter,
  makeSelectTimezone,
  makeSelectTripPreferences,
  makeSelectTripCancellationReasons,
  makeSelectTripDenialReasons,
  makeSelectVehicleCategories,
  makeSelectSidebarMenuCounts,
  makeSelectIntegrationState,
  makeSelectTripEscalationReasons,
  makeSelectTripApprovalReasons,
  makeSelectTripGroupingState,
  makeSelectMemberEnrollmentCoverageTripCounts,
  makeSelectGlobalSearch,
  makeSelectToastNotifications,
  makeSelectLoadingUser,
};
